import { useMemo } from 'react';
import { useApi, usePrefsValue, useSession } from '@adiox/ui';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import { createTradeClient, type TradeClient } from './tradeClient';
import { tradeBlockReason } from './tradeRules';

/**
 * The trading account the terminal acts on: the one the user picked (kept in their synced preferences) if it is still theirs,
 * otherwise their newest account, which for a prop-firm client is the current stage.
 */
export function useActiveLogin(): number | undefined {
  const accounts = useSession().user?.accounts;
  const chosen = usePrefsValue((s) => s.data.activeLogin);
  return pickActiveLogin(accounts, typeof chosen === 'number' ? chosen : undefined);
}

export function pickActiveLogin(
  accounts: readonly number[] | undefined,
  chosen: number | undefined,
): number | undefined {
  if (!accounts || accounts.length === 0) return undefined;
  if (chosen !== undefined && accounts.includes(chosen)) return chosen;
  return Math.max(...accounts);
}

export function useTradeClient(): TradeClient | null {
  const api = useApi();
  const login = useActiveLogin();
  return useMemo(() => (login === undefined ? null : createTradeClient(api, login)), [api, login]);
}

/** Everything the ticket needs to know about one symbol right now, including why trading may not be possible. */
export function useTradeGate(symbolName: string | null) {
  const login = useActiveLogin();
  const readOnly = useSession().user?.readOnly === true;
  const symbols = useSymbols();
  const info = symbols.data?.find((s) => s.name === symbolName);
  const quote = useStream((s) => (symbolName ? s.quotes[symbolName] : undefined));
  const hubState = useStream((s) => s.hubState);
  const feedUp = useStream((s) => s.feedUp);
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const reason = tradeBlockReason({
    hubState,
    feedUp,
    accountLoaded: account?.loaded ?? false,
    tradingEnabled: account?.tradingEnabled ?? true,
    quote,
    symbol: info,
    readOnly,
  });
  return { reason, quote, info, account, login, readOnly, hubOnline: hubState === 'connected' };
}
